import { gql } from '@apollo/client';

import { NESTED_MENU_MAX_DEPTH } from '@/components/Nav';
import { query } from '@/lib/ApolloClient';
import { Menu } from '@/types';

// Type for nested menu structure
export interface NestedMenu extends Omit<Menu, 'MenuItems'> {
  MenuItems?: NestedMenuItem[];
}

export interface NestedMenuItem {
  __typename?: string;
  id?: string | number;
  // For nested submenus - this will be the full nested menu, not just a reference
  MenuKey?: string;
  MenuName?: string;
  IsRootMenu?: boolean | null;
  MenuItems?: NestedMenuItem[];
  // CustomLabel fields from different components (with actual field names from query)
  SubMenuCustomLabel?: string | null;
  ResourceCustomLabel?: string | null;
  LinkCustomLabel?: string | null;
  CustomLabel?: string | null; // For page links
  // PageAnchor from various components
  PageAnchor?: string | null;
  ResourcePageAnchor?: string | null; // Aliased PageAnchor for resource items
  // Relations
  SubMenu?: NestedMenu;
  resource_article?: {
    __typename?: string;
    Slug?: string;
    ResourceTitle?: string;
    ResourceCategory?: {
      __typename?: string;
      Slug?: string;
      CategoryName?: string;
    };
  };
  resource_category?: {
    __typename?: string;
    Slug?: string;
    CategoryName?: string;
  };
  Page?: {
    __typename?: string;
    Slug?: string;
    Title?: string;
    PageCategory?: {
      __typename?: string;
      CategoryName?: string;
      Slug?: string;
    } | null;
  };
  Url?: string;

  // Any other menu item properties
  [key: string]: any;
}

export const getMenus = async (draftModeEnabled: boolean = false) => {
  // Use a simpler query that fetches all menus and their items
  const pageQuery = gql`
    query Menus($status: PublicationStatus) {
      menus(status: $status) {
        MenuKey
        MenuName
        IsRootMenu
        MenuItems {
          ... on ComponentMenuMenuSubMenu {
            SubMenuCustomLabel: CustomLabel
            SubMenu {
              MenuName
              MenuKey
            }
          }
          ... on ComponentMenuMenuLinkResource {
            id
            ResourceCustomLabel: CustomLabel
            ResourcePageAnchor: PageAnchor
            resource_article {
              Slug
              ResourceTitle
              ResourceCategory {
                Slug
                CategoryName
              }
            }
          }
          ... on ComponentMenuMenuLinkResourceCategory {
            ResourceCustomLabel: CustomLabel
            ResourcePageAnchor: PageAnchor
            resource_category {
              Slug
              CategoryName
            }
          }
          ... on ComponentMenuMenuLinkUrl {
            Url
            LinkCustomLabel: CustomLabel
          }
          ... on ComponentMenuMenuLinkPage {
            Page {
              Slug
              Title
              PageCategory {
                CategoryName
                Slug
                ParentCategory {
                  Slug
                  ParentCategory {
                    Slug
                    ParentCategory {
                      Slug
                    }
                  }
                }
              }
            }
            PageAnchor
            CustomLabel
          }
        }
      }
    }
  `;

  return await query<{ menus: Menu[] }>({
    query: pageQuery,
    variables: {
      status: draftModeEnabled ? 'DRAFT' : 'PUBLISHED',
    },
  });
};

export const nestMenus = (
  menus: Menu[],
  maxDepth: number = NESTED_MENU_MAX_DEPTH,
  removeSubMenus: boolean = false
): NestedMenu[] => {
  // Filter out non-root menus and the footer-resources menu
  const rootMenus = menus.filter((menu) => menu.IsRootMenu && menu.MenuKey !== 'footer-resources');

  const processMenuItems = (
    menuItems: any[] = [],
    depth: number = 0,
    processedInThisBranch: Set<string> = new Set()
  ): NestedMenuItem[] => {
    if (depth >= maxDepth) return [];

    return (menuItems ?? []).filter(Boolean).flatMap((item) => {
      if (item.__typename === 'ComponentMenuMenuSubMenu') {
        if (removeSubMenus) {
          return [];
        }

        if (item.SubMenu?.MenuItems) {
          return [
            {
              ...item,
              SubMenu: {
                ...item.SubMenu,
                MenuItems: processMenuItems(
                  item.SubMenu.MenuItems,
                  depth + 1,
                  processedInThisBranch
                ),
              },
            } as NestedMenuItem,
          ];
        }

        if (item.SubMenu?.MenuKey) {
          const subMenuKey = item.SubMenu.MenuKey;
          if (processedInThisBranch.has(subMenuKey)) return [item as NestedMenuItem];

          const referencedMenu = menus.find((menu) => menu.MenuKey === subMenuKey);
          if (referencedMenu) {
            const newBranchSet = new Set(processedInThisBranch);
            newBranchSet.add(subMenuKey);

            return [
              {
                ...item,
                SubMenu: {
                  ...referencedMenu,
                  MenuItems: referencedMenu.MenuItems
                    ? processMenuItems(referencedMenu.MenuItems, depth + 1, newBranchSet)
                    : [],
                },
              } as NestedMenuItem,
            ];
          }
        }
      }

      return [item as NestedMenuItem];
    });
  };

  return rootMenus.map(
    (targetMenu): NestedMenu => ({
      ...targetMenu,
      MenuItems: targetMenu.MenuItems
        ? processMenuItems(Array.isArray(targetMenu.MenuItems) ? targetMenu.MenuItems : [])
        : undefined,
    })
  );
};

export const getMenuItemLabel = (item: NestedMenuItem): string | null | undefined => {
  return (
    item.SubMenuCustomLabel || item.ResourceCustomLabel || item.LinkCustomLabel || item.CustomLabel
  );
};
